import {Component, Inject, NgZone, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {BehaviorSubject, catchError, filter, forkJoin, of, Subject, tap} from 'rxjs';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Job} from '@modules/job/models/job.model';
import {ExtraJob} from '@modules/extra-job/models/extra-job.model';
import {JobCheckoutComponent} from '@modules/job/pages/job-checkout/job-checkout.component';
import {ExtraJobCheckoutComponent} from '@modules/extra-job/pages/extra-job-checkout/extra-job-checkout.component';
import {PaymentService} from '@shared/component/payments/services/payment.service';
import {EventSubscription, PusherService} from '@shared/services/pusher.service';
import {AppEventEnum, PaymentStatusEnum} from '@generated/graphql';
import {SnackBarService} from '@shared/services/snack-bar.service';
import {BusinessPlanService} from '@modules/account-settings/pages/business-profile/services/business-plan.service';
import {PaymentProviderService} from '@modules/payment/services/payment-provider.service';

@UntilDestroy()
@Component({
  selector: 'app-checkout-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatProgressSpinnerModule, JobCheckoutComponent, ExtraJobCheckoutComponent],
  templateUrl: './checkout-dialog.component.html',
  styleUrls: ['./checkout-dialog.component.scss']
})
export class CheckoutDialogComponent implements OnInit, OnDestroy {
  readonly paid$ = new Subject<void>();
  readonly ready$ = new BehaviorSubject(false);
  private paymentStatusSubscription: EventSubscription | null = null;
  private paymentId: string | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: { job?: Job; extraJob?: ExtraJob; },
    private readonly businessPlanService: BusinessPlanService,
    private readonly paymentProviderService: PaymentProviderService,
    private readonly paymentService: PaymentService,
    private readonly pusherService: PusherService,
    private readonly snackBarService: SnackBarService,
    private readonly dialogRef: MatDialogRef<CheckoutDialogComponent>,
    private readonly ngZone: NgZone
  ) {
  }

  ngOnInit(): void {
    this.pusherService.setup();
    this.ensureCheckoutDataIsFresh();

    this.paymentService.paid$.pipe(
      filter(event => !!this.paymentId && event.paymentId === this.paymentId),
      tap(() => this.paid$.next()),
      tap(() => this.ngZone.run(() => this.dialogRef.close())),
      untilDestroyed(this)
    ).subscribe();

    this.paymentId = this.data.job?.order?.payment?.id ?? this.data.extraJob?.order?.payment?.id ?? null;

    this.subscribeToProcessingUpdates();
    this.subscribeToPaymentUpdates();
  }

  ngOnDestroy(): void {
    this.paymentStatusSubscription?.leaveChannel();
    this.paid$.complete();
    this.ready$.complete();
  }

  private subscribeToPaymentUpdates(): void {
    const paymentId = this.paymentId;

    if (!paymentId) {
      return;
    }

    this.paymentStatusSubscription = this.pusherService.subscribe(
      `payments.${paymentId}`,
      AppEventEnum.PaymentStatusUpdated,
      payload => {
        const outcome = payload.outcome as string | undefined;
        const shouldClearProcessing = outcome === 'failed' || outcome === 'rejected';

        if (shouldClearProcessing) {
          this.paymentService.clearProcessing(payload.paymentId);
          if (payload.orderId) {
            this.paymentService.clearOrderProcessing(payload.orderId);
            this.paymentService.clearOrderAwaitingRefresh(payload.orderId);
          }
        }

        if (payload.outcome === 'succeeded') {
          this.snackBarService.pushSuccessAlert('Payment successful.');
          if (payload.paymentId) {
            this.paymentService.paid$.next({ paymentId: payload.paymentId });
          }
          return;
        }

        if (payload.outcome === 'failed') {
          if (payload.message) {
            this.snackBarService.pushErrorAlert(payload.message);
          } else {
            this.snackBarService.pushSubmitPaymentFailMessage();
          }
        }
      }
    );
  }

  private subscribeToProcessingUpdates(): void {
    if (!this.paymentId) {
      return;
    }

    this.paymentService.processing$.pipe(
      filter(event => event.paymentId === this.paymentId),
      tap(() => {
        this.snackBarService.pushAlert('Payment is processing. We will update you shortly.', 'Processing');
        this.ngZone.run(() => this.dialogRef.close());
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  private ensureCheckoutDataIsFresh(): void {
    forkJoin([
      this.businessPlanService.ensureFresh$().pipe(
        catchError((e) => {
          console.error(e);

          return of(null);
        }),
      ),
      this.paymentProviderService.ensureFresh$().pipe(
        catchError((e) => {
          console.error(e);

          return of([]);
        }),
      ),
    ]).pipe(
      tap(() => this.ready$.next(true)),
      untilDestroyed(this),
    ).subscribe();
  }
}
